import React, { useState, useCallback, useMemo, useContext, Fragment } from 'react';
import { useIntl } from 'react-intl-next';
import { useQuery } from '@apollo/react-hooks';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import { ModalTransition } from '@atlaskit/modal-dialog';
import { N800, N0 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { TooltipPrimitive } from '@atlaskit/tooltip';

import { fg } from '@confluence/feature-gating';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { markErrorAsHandled } from '@confluence/graphql';
import {
	ErrorDisplay,
	isUnauthorizedError,
	withErrorBoundary,
	Attribution,
} from '@confluence/error-boundary';
import { containsNoNetworkError } from '@confluence/network';
import type { FlagsStateContainer } from '@confluence/flags';
import { ShortcutListener, EDITOR2_SHORTCUTS } from '@confluence/shortcuts';
import { onChangeTitleDebounceTime } from '@confluence/editor-title';
import type { ContentRestrictionType } from '@confluence/restrictions';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';

import { DefaultPublishModalButton } from './DefaultPublishModalButton';
import { UpdateSplitButton } from './UpdateSplitButton';
import { ScheduledButton } from './ScheduledButton';
import { PublishModal, DiscardModal } from './PublishModal';
import { usePublishModalAnalyticsEvent } from './usePublishModalAnalyticsEvent';
import { ValidatePagePublishQuery } from './ValidatePagePublishQuery.graphql';
import { displayPublishErrorFlag } from './helpers';
import { SHORTCUT_KEYS } from './constants';
import { PublishModalContext, withPublishModalContextProvider } from './PublishModalContext';
import type { PublishModalContextProviderExternalProps } from './PublishModalContext';
import { PermissionsDialog } from './PermissionsDialog';
import { ShareAndRestrictDialogInPublishModal } from './ShareAndRestrictDialogInPublishModal';
import { usePublishModalActions } from './usePublishModalActions';

// same styles as a tooltip but a larger max width to accomodate the long content and shortcut
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
export const WideTooltip = styled(TooltipPrimitive)`
	background: ${token('color.background.neutral.bold', N800)};
	color: ${token('color.text.inverse', N0)};
	border-radius: 3px;
	font-style: normal;
	font-weight: 400;
	font-size: 12px;
	max-width: 350px;
	padding: ${token('space.025', '2px')} ${token('space.075', '6px')};
	line-height: 1.3;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
export const TooltipText = styled.div`
	display: flex;
	align-items: center;
	gap: ${token('space.025', '2px')};
`;

export type PublishButtonWithMoreOptionsProps = PublishModalContextProviderExternalProps &
	PublishButtonWithMoreOptionsUnwrappedProps;

type PublishButtonWithMoreOptionsUnwrappedProps = {
	publishButtonDisabled: boolean | undefined;
	isTitleEmpty: boolean;
	userViewingPreview?: boolean;
	isUnpublishedDraft: boolean;
	flags: FlagsStateContainer;
	saveDraft: (() => void) | undefined;
	contentType: string;
	title: string | null;
	savingInProgress: boolean;
	shouldShowWorldIcon: boolean;
	shouldShowFooterButtons?: boolean;
};

export type AutoFocusTargetOptions = undefined | 'version comment' | 'access' | 'location';

const PublishButtonWithMoreOptionsUnwrapped = ({
	publishButtonDisabled,
	isTitleEmpty,
	title,
	userViewingPreview,
	isUnpublishedDraft,
	flags,
	saveDraft,
	contentType,
	savingInProgress,
	shouldShowWorldIcon,
	shouldShowFooterButtons = true,
}: PublishButtonWithMoreOptionsUnwrappedProps) => {
	const intl = useIntl();
	const [contentId] = usePageContentId();
	const {
		currentlyScheduledPublishDate,
		setExpanded,
		graphqlError,
		restrictionsGraphQLError,
		discardCurrentState,
		hasModifiedSettings,
		refetchRestrictions,
	} = useContext(PublishModalContext);
	const { handlePreview } = usePublishModalActions();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [discardModalIsOpen, setDiscardModalIsOpen] = useState<boolean>(false);
	const [focusField, setFocusField] = useState<AutoFocusTargetOptions>();
	/*
	 * We have to deactivate the keyboard shortcuts for publish/update button when the publish modal is open
	 * so that there aren't duplicate shortcuts registered.
	 */
	const [shortcutIsEnabled, setShortcutIsEnabled] = useState<boolean>(true);

	/**
	 * Target a field to focus on modal open.
	 */
	const [autoFocusTarget, setAutoFocusTarget] = useState<AutoFocusTargetOptions>();

	const [permissionsDialogIsOpen, setPermissionsDialogIsOpen] = useState(false);
	const [selectedRestrictionType, setSelectedRestrictionType] = useState<
		ContentRestrictionType | undefined
	>(undefined);
	const openPermissionsDialog = (chosenContentRestrictionType: ContentRestrictionType) => {
		setPermissionsDialogIsOpen(true);
		setSelectedRestrictionType(chosenContentRestrictionType);
	};
	const closePermissionsDialog = () => {
		setPermissionsDialogIsOpen(false);
		setSelectedRestrictionType(undefined);
	};

	// Changes to EditorTitle are debounced if the page is a draft, which we need to account for
	const titleDebounceTime = isUnpublishedDraft ? onChangeTitleDebounceTime : 0;

	const { createAnalyticsEvent } = usePublishModalAnalyticsEvent();

	const { refetch: checkPageValidation, error: validatePagePublishQueryError } = useQuery(
		ValidatePagePublishQuery,
		{
			errorPolicy: 'all',
			fetchPolicy: 'network-only',
			variables: {
				contentId,
				type: contentType,
				title,
			},
			skip: true,
		},
	);

	const errorToReport = useMemo(() => {
		const handleError = (e) => {
			if (e) {
				if (containsNoNetworkError(e) || isUnauthorizedError(e)) {
					markErrorAsHandled(e);
					return undefined;
				}
				return e;
			}
		};

		if (graphqlError) {
			return handleError(graphqlError);
		}
		if (restrictionsGraphQLError) {
			return handleError(restrictionsGraphQLError);
		}
		if (validatePagePublishQueryError) {
			return handleError(validatePagePublishQueryError);
		}
	}, [validatePagePublishQueryError, restrictionsGraphQLError, graphqlError]);

	/*
	 * The openModal function below opens the publish modal.
	 * It is sometimes used to return to the publish modal after its sub-dialogs (like the permissions dialog) are closed.
	 *
	 * validatePageAndOpenModal checks if publish is valid before opening the publish modal
	 * It should only be used when opening modal from user action (clicking a button, tooltip shortcut, etc.)
	 */
	const openModal = useCallback((focus?: AutoFocusTargetOptions) => {
		setShortcutIsEnabled(false);
		setIsOpen(true);
		setFocusField(focus);
	}, []);
	const validatePageAndOpenModal = useCallback(
		(actionSubjectId: string) => {
			const validatePage = async () => {
				const { data } = await checkPageValidation();
				if (!data.validatePagePublish?.isValid) {
					await displayPublishErrorFlag({
						validationMessage: data.validatePagePublish?.message,
						isTitleEmpty,
						flags,
						formatMessage: intl.formatMessage,
					});
				} else {
					openModal();
				}
			};
			return async () => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						actionSubject: 'button',
						action: 'clicked',
						actionSubjectId,
						source: 'PublishButtonWithMoreOptions',
						attributes: {
							isUnpublishedDraft,
						},
					},
				}).fire();
				setTimeout(async () => {
					saveDraft?.();
					refetchRestrictions();
					await validatePage();
				}, titleDebounceTime);
			};
		},
		[
			createAnalyticsEvent,
			openModal,
			checkPageValidation,
			flags,
			intl.formatMessage,
			isTitleEmpty,
			titleDebounceTime,
			saveDraft,
			refetchRestrictions,
			isUnpublishedDraft,
		],
	);

	const closeModal = useCallback(() => {
		setIsOpen(false);
	}, []);

	const requestCloseModal = () => {
		if (hasModifiedSettings) {
			setDiscardModalIsOpen(true);
		} else {
			discardCurrentState();
			closeModal();
		}
	};

	const closeDiscardModal = useCallback(() => {
		setDiscardModalIsOpen(false);
	}, []);

	const onCloseComplete = useCallback(() => {
		/** We can only re-enabled the keyboard shortcut when the modal is finished closing,
		 *  because otherwise a duplicate shortcut will exist during the modal close animation.
		 */
		setShortcutIsEnabled(true);
	}, []);

	const [spaceKey] = usePageSpaceKey();

	const getPrimaryButton = () => {
		if (!isUnpublishedDraft && !currentlyScheduledPublishDate) {
			const openModal = isCompanyHubSpaceKey(spaceKey)
				? undefined
				: validatePageAndOpenModal('PublishModalChevronButton');
			return (
				<>
					<UpdateSplitButton
						openModal={openModal}
						publishButtonDisabled={publishButtonDisabled}
						shortcutIsEnabled={shortcutIsEnabled}
						saveDraft={saveDraft}
						isTitleEmpty={isTitleEmpty}
						flags={flags}
						savingInProgress={savingInProgress}
						shouldShowWorldIcon={shouldShowWorldIcon}
						contentType={contentType}
					/>
					{shortcutIsEnabled && !savingInProgress && (
						<>
							{!publishButtonDisabled && openModal && (
								<ShortcutListener
									key={SHORTCUT_KEYS.PBWMO1}
									accelerator={EDITOR2_SHORTCUTS.PUBLISH_WITH_COMMENT}
									listener={() => {
										setAutoFocusTarget('version comment');
										void openModal();
									}}
								/>
							)}
							<ShortcutListener
								key={SHORTCUT_KEYS.PBWMO2}
								accelerator={EDITOR2_SHORTCUTS.PREVIEW}
								listener={() =>
									handlePreview({
										analyticsSource: 'KeyboardShortcut',
										isUnpublishedDraft,
									})
								}
							/>
						</>
					)}
				</>
			);
		} else
			return (
				<Fragment>
					{!!currentlyScheduledPublishDate ? (
						<ScheduledButton
							saveDraft={saveDraft}
							openModalWithAnalytics={validatePageAndOpenModal}
							savingInProgress={savingInProgress}
							isUnpublishedDraft={isUnpublishedDraft}
							shouldShowWorldIcon={shouldShowWorldIcon}
							publishButtonDisabled={publishButtonDisabled}
						/>
					) : (
						<DefaultPublishModalButton
							saveDraft={saveDraft}
							openModalWithAnalytics={validatePageAndOpenModal}
							savingInProgress={savingInProgress}
							publishButtonDisabled={publishButtonDisabled}
						/>
					)}
					{shortcutIsEnabled && !savingInProgress && (
						<>
							<ShortcutListener
								key={SHORTCUT_KEYS.PBWMO3}
								accelerator={EDITOR2_SHORTCUTS.PUBLISH}
								listener={validatePageAndOpenModal(
									!!currentlyScheduledPublishDate
										? 'ScheduledButton'
										: 'PublishModalPublishMoreActionsButton',
								)}
							/>
							<ShortcutListener
								key={SHORTCUT_KEYS.PBWMO4}
								accelerator={EDITOR2_SHORTCUTS.PUBLISH_WITH_COMMENT}
								listener={() => {
									setAutoFocusTarget('version comment');
									setExpanded(true);
									void validatePageAndOpenModal(
										!!currentlyScheduledPublishDate
											? 'ScheduledButton'
											: 'PublishModalPublishMoreActionsButton',
									)();
								}}
							/>
							<ShortcutListener
								key={SHORTCUT_KEYS.PBWMO5}
								accelerator={EDITOR2_SHORTCUTS.PREVIEW}
								listener={() =>
									handlePreview({
										analyticsSource: 'KeyboardShortcut',
										isUnpublishedDraft,
									})
								}
							/>
						</>
					)}
				</Fragment>
			);
	};

	return (
		<Fragment>
			{getPrimaryButton()}
			<ModalTransition>
				{isOpen && (
					<PublishModal
						publishButtonDisabled={publishButtonDisabled}
						closeModal={closeModal}
						openModal={openModal}
						requestCloseModal={requestCloseModal}
						onCloseComplete={onCloseComplete}
						isUnpublishedDraft={isUnpublishedDraft}
						openPermissionsDialog={openPermissionsDialog}
						userViewingPreview={userViewingPreview}
						autoFocusTarget={autoFocusTarget}
						setAutoFocusTarget={setAutoFocusTarget}
						title={title}
						focusField={focusField}
						setFocusField={setFocusField}
						shouldShowWorldIcon={shouldShowWorldIcon}
						shouldShowFooterButtons={shouldShowFooterButtons}
					/>
				)}
			</ModalTransition>
			{fg('confluence_frontend_unified_restrict_and_share') ? (
				permissionsDialogIsOpen && ( // ShareAndRestrictDialogController must be rendered outside of a ModalTransition, as it is not a Modal directly.
					<ShareAndRestrictDialogInPublishModal
						contentId={contentId!}
						contentType={contentType as 'page' | 'blogpost'}
						onClose={() => {
							closePermissionsDialog();
							openModal('access');
						}}
						isUnpublishedDraft={isUnpublishedDraft}
					/>
				)
			) : (
				<ModalTransition>
					{permissionsDialogIsOpen && (
						<PermissionsDialog
							onClose={() => {
								closePermissionsDialog();
								openModal('access');
							}}
							selectedRestrictionType={selectedRestrictionType}
							contentId={contentId!}
						/>
					)}
				</ModalTransition>
			)}
			<ModalTransition>
				{discardModalIsOpen && (
					<DiscardModal
						closeModal={closeModal}
						closeDiscardModal={closeDiscardModal}
						isUnpublishedDraft={isUnpublishedDraft}
					/>
				)}
			</ModalTransition>
			{errorToReport && <ErrorDisplay error={errorToReport} />}
		</Fragment>
	);
};

export const PublishButtonWithMoreOptions = withErrorBoundary({
	attribution: Attribution.PAGE_MODES,
})(withPublishModalContextProvider(PublishButtonWithMoreOptionsUnwrapped));
